import {
  CLIENTES, COLABORADORES, FATURAMENTO, MATURIDADE, OPERACOES, SEGMENTOS, SETUP_BLOCKS, blockFilled, setupReadiness,
  type Objetivo, type SetupAnswers, type SetupBlockKey,
} from '@ads/shared';
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router';
import { BareInput, SelectField, TextArea, TextField } from '../components/Field';
import * as Ic from '../components/icons';
import { Instrument, type InstrumentHandle } from '../components/Instrument';
import { Actions, useViewMeta } from '../components/Shell';
import { useSaveSetup, useSetup } from '../lib/queries';
import { BoardBlock } from './InicioBoard';
import { Materiais, ObjectivePanel, useObjectiveReading } from './InicioPanels';

const OP_HINT: Record<string, string> = { B2B: 'vende para empresas', B2C: 'para pessoas', B2G: 'para o governo' };

/* ─────────────────────────────────────────────────────────────────────
   Início — Etapa 01 do setup. O instrumento lê a organização enquanto
   ela se apresenta. Cada bloco se salva sozinho, pouco depois de parar
   de digitar; nada se perde ao trocar de tela.
   ───────────────────────────────────────────────────────────────────── */

const DELAY = 700;

function useAutosave(answers: SetupAnswers | null) {
  const save = useSaveSetup();
  const timers = useRef(new Map<SetupBlockKey, ReturnType<typeof setTimeout>>());
  const pending = useRef(new Map<SetupBlockKey, unknown>());
  const last = useRef<SetupAnswers | null>(null);

  const flushOne = useCallback((k: SetupBlockKey) => {
    const data = pending.current.get(k);
    if (data === undefined) return Promise.resolve();
    pending.current.delete(k);
    clearTimeout(timers.current.get(k));
    return save.mutateAsync({ block: k, data: data as never }).catch(() => { pending.current.set(k, data); });
  }, [save]);

  const flush = useCallback(() => Promise.all([...pending.current.keys()].map(flushOne)), [flushOne]);

  useEffect(() => {
    if (!answers) return;
    const prev = last.current;
    last.current = answers;
    if (!prev) return;
    for (const b of SETUP_BLOCKS) {
      if (prev[b.key] === answers[b.key]) continue;
      pending.current.set(b.key, answers[b.key]);
      clearTimeout(timers.current.get(b.key));
      timers.current.set(b.key, setTimeout(() => void flushOne(b.key), DELAY));
    }
  }, [answers, flushOne]);

  /* saiu da tela: grava o que faltou */
  useEffect(() => () => { void flush(); }, [flush]);
  return { flush, saving: save.isPending };
}

export function Inicio() {
  useViewMeta({ nav: 'inicio', crumb: ['Setup', <>Etapa <span className="n">01</span> — Identificação</>], j: .09 });
  const setup = useSetup();
  const nav = useNavigate();
  const [answers, setAnswers] = useState<SetupAnswers | null>(null);
  const [open, setOpen] = useState<Record<SetupBlockKey, boolean>>({ identificacao: true, perfil: false, canais: false, objetivo: false, materiais: false, board: false });
  const [savedFlash, setSavedFlash] = useState(false);
  const dial = useRef<InstrumentHandle | null>(null);
  const [shownPct, setShownPct] = useState(0);

  useEffect(() => { if (setup.data && !answers) setAnswers(setup.data.answers); }, [setup.data, answers]);
  const { flush } = useAutosave(answers);

  const readiness = useMemo(() => (answers ? setupReadiness(answers) : { pct: 0, fill: SETUP_BLOCKS.map(() => 0), complete: 0 }), [answers]);

  const set = <K extends SetupBlockKey>(k: K, patch: Partial<SetupAnswers[K]>) =>
    setAnswers(a => (a ? { ...a, [k]: { ...a[k], ...patch } } : a));
  const setObjetivo = useCallback((patch: Partial<Objetivo>) =>
    setAnswers(a => (a ? { ...a, objetivo: { ...a.objetivo, ...patch } } : a)), []);
  const reading = useObjectiveReading(answers?.objetivo.goal ?? '', answers?.objetivo ?? null, setObjetivo);
  const pulse = (k: SetupBlockKey) => () => dial.current?.pulse(SETUP_BLOCKS.findIndex(b => b.key === k));

  const remaining = SETUP_BLOCKS.reduce((s, b) => {
    if (!answers || !b.fields.length) return s;
    const { done, total } = blockFilled(b.key, answers[b.key] as Record<string, unknown>);
    return s + (total - done);
  }, 0);
  const minutes = Math.max(1, Math.ceil(remaining * .35));
  const openCount = Object.values(open).filter(Boolean).length;

  if (!answers) return <section className="view on" data-v="inicio"><div className="loading-line" /></section>;
  const a = answers;

  const counter = (k: SetupBlockKey) => {
    const { done, total } = blockFilled(k, a[k] as Record<string, unknown>);
    return (
      <span className={`cnt-b${done === total ? ' done' : ''}`}><Ic.Check />{done} de {total}</span>
    );
  };
  const details = (k: SetupBlockKey, i: number) => ({
    open: open[k],
    onToggle: (e: React.SyntheticEvent<HTMLDetailsElement>) => {
      const isOpen = e.currentTarget.open;
      setOpen(o => (o[k] === isOpen ? o : { ...o, [k]: isOpen }));
    },
    style: { '--p': SETUP_BLOCKS[i]!.fields.length ? readiness.fill[i]!.toFixed(4) : 0 } as CSSProperties,
  });

  return (
    <section className="view on" data-v="inicio">
      <section className="panel">
        <div className="corners" aria-hidden="true"><span className="tl" /><span className="tr" /><span className="br" /><span className="bl" /></div>
        <div>
          <div className="eyebrow"><span className="rule" /><span>Setup</span><b>01 / 11</b></div>
          <h1>
            <span className="ln"><i>Apresente</i></span>
            <span className="ln"><i>sua organização</i></span>
            <span className="ln"><i>ao <span className="ads">ADS</span>.</i></span>
          </h1>
          <p className="lede">Um site, seus canais, alguns documentos e o desafio que você quer resolver. O resto o ADS deduz.</p>
          <div className="facts">
            <div className="fact"><span className="k mono">Prontidão</span><span className="v">{shownPct}<small>%</small></span></div>
            <div className="fact"><span className="k mono">Blocos completos</span><span className="v">{readiness.complete}<small>/{SETUP_BLOCKS.filter(b => b.fields.length).length}</small></span></div>
            <div className="fact"><span className="k mono">Tempo estimado</span><span className="v">{remaining ? minutes : 0}<small> min</small></span></div>
          </div>
        </div>
        <div className="instrument">
          <Instrument fill={readiness.fill} weights={SETUP_BLOCKS.map(b => b.weight)} pct={readiness.pct} onPct={setShownPct} pulseRef={dial} />
          <div className="readout">
            <div className="big"><span>{shownPct}</span><sup>%</sup></div>
            <div className="cap mono">Leitura</div>
          </div>
          <div className="foot mono"><i />Analisando em tempo real</div>
        </div>
      </section>

      <div className="blocks">
        <div className="blocks-head">
          <h2>Blocos do setup</h2>
          <span className="meta mono">{openCount === 0 ? 'nenhum aberto' : `${openCount} de ${SETUP_BLOCKS.length} aberto${openCount > 1 ? 's' : ''}`}</span>
        </div>

        <details {...details('identificacao', 0)}>
          <summary>
            <span className="no mono">01</span><span className="t">Identificação da organização</span>
            <span className="st mono">{counter('identificacao')}<span className="tog" aria-hidden="true"><i /><i /></span></span>
          </summary>
          <div className="body">
            <div className="grid c2">
              <TextField id="org" label="Nome da organização" value={a.identificacao.org} placeholder="Ex.: Escola Aurora" autoComplete="organization"
                onChange={v => set('identificacao', { org: v })} onRead={pulse('identificacao')} />
              <TextField id="site" label="Website" type="url" value={a.identificacao.site} placeholder="suaorganizacao.com"
                onChange={v => set('identificacao', { site: v })} onRead={pulse('identificacao')} />
              <TextField id="loc" label="Localidade" value={a.identificacao.loc} placeholder="Ex.: Porto Alegre, RS"
                onChange={v => set('identificacao', { loc: v })} onRead={pulse('identificacao')} />
              <SelectField id="seg" label="Segmento" value={a.identificacao.seg} options={SEGMENTOS} placeholder="Selecione o segmento"
                onChange={v => set('identificacao', { seg: v as never })} onRead={pulse('identificacao')} />
            </div>
          </div>
        </details>

        <details {...details('perfil', 1)}>
          <summary>
            <span className="no mono">02</span><span className="t">Perfil do negócio</span>
            <span className="st mono">{counter('perfil')}<span className="tog" aria-hidden="true"><i /><i /></span></span>
          </summary>
          <div className="body">
            <div className="grid c3">
              <div className="f">
                <span className="lab" id="lbl-op">Tipo de operação<span className="opt">uma ou mais</span></span>
                <div className="chips" role="group" aria-labelledby="lbl-op">
                  {OPERACOES.map(o => {
                    const on = a.perfil.op.includes(o);
                    return (
                      <button key={o} type="button" aria-pressed={on} title={`${o}: ${OP_HINT[o]}`}
                        onClick={() => { set('perfil', { op: on ? a.perfil.op.filter(x => x !== o) : OPERACOES.filter(x => x === o || a.perfil.op.includes(x)) }); if (!on && !a.perfil.op.length) pulse('perfil')(); }}>
                        {on && <Ic.Check />}{o}
                      </button>
                    );
                  })}
                </div>
                <span className="f-hint">{a.perfil.op.length ? a.perfil.op.map(o => `${o} ${OP_HINT[o]}`).join(' · ').replace(/^./, c => c.toUpperCase()) : 'B2B vende para empresas · B2C para pessoas · B2G para o governo'}</span>
              </div>
              <SelectField id="team" label="Colaboradores" value={a.perfil.team} options={COLABORADORES} onChange={v => set('perfil', { team: v as never })} onRead={pulse('perfil')} />
              <SelectField id="rev" label="Faturamento" opt="faixa" value={a.perfil.rev} options={FATURAMENTO} onChange={v => set('perfil', { rev: v as never })} onRead={pulse('perfil')} />
              <SelectField id="cli" label="Clientes ativos" opt="faixa" value={a.perfil.cli} options={CLIENTES} onChange={v => set('perfil', { cli: v as never })} onRead={pulse('perfil')} />
              <SelectField id="mat" label="Maturidade digital" value={a.perfil.mat} options={MATURIDADE} onChange={v => set('perfil', { mat: v as never })} onRead={pulse('perfil')} />
            </div>
          </div>
        </details>

        <details {...details('canais', 2)}>
          <summary>
            <span className="no mono">03</span><span className="t">Canais e presença digital</span>
            <span className="st mono">{counter('canais')}<span className="tog" aria-hidden="true"><i /><i /></span></span>
          </summary>
          <div className="body">
            <div className="chan">
              <div className="chan-row"><span className="chan-name"><Ic.Instagram />Instagram</span>
                <BareInput label="Instagram" value={a.canais.instagram} placeholder="instagram.com/suaorg" onChange={v => set('canais', { instagram: v })} onRead={pulse('canais')} /></div>
              <div className="chan-row"><span className="chan-name"><Ic.LinkedIn />LinkedIn</span>
                <BareInput label="LinkedIn" value={a.canais.linkedin} placeholder="linkedin.com/company/suaorg" onChange={v => set('canais', { linkedin: v })} onRead={pulse('canais')} /></div>
              <div className="chan-row"><span className="chan-name"><Ic.YouTube />YouTube</span>
                <BareInput label="YouTube" value={a.canais.youtube} placeholder="youtube.com/@suaorg" onChange={v => set('canais', { youtube: v })} onRead={pulse('canais')} /></div>
              {a.canais.extra.map((x, i) => (
                <div className="chan-row" key={i}>
                  <span className="chan-name chan-extra">
                    <Ic.Link />
                    <input value={x.label} placeholder="Canal" aria-label="Nome do canal" maxLength={60}
                      onChange={e => set('canais', { extra: a.canais.extra.map((y, j) => (j === i ? { ...y, label: e.target.value } : y)) })} />
                  </span>
                  <div className="chan-inp">
                    <BareInput label={x.label || 'Canal'} value={x.url} placeholder="endereço do canal"
                      onChange={v => set('canais', { extra: a.canais.extra.map((y, j) => (j === i ? { ...y, url: v } : y)) })} />
                    <button type="button" className="chan-x" aria-label="Remover canal"
                      onClick={() => set('canais', { extra: a.canais.extra.filter((_, j) => j !== i) })}><Ic.Close /></button>
                  </div>
                </div>
              ))}
              {a.canais.extra.length < 8 && (
                <button className="addlink" type="button" onClick={() => set('canais', { extra: [...a.canais.extra, { label: '', url: '' }] })}>
                  <Ic.Plus />Adicionar outro canal
                </button>
              )}
            </div>
          </div>
        </details>

        <details {...details('objetivo', 3)}>
          <summary>
            <span className="no mono">04</span><span className="t">Objetivo e contexto</span>
            <span className="st mono">{counter('objetivo')}<span className="tog" aria-hidden="true"><i /><i /></span></span>
          </summary>
          <div className="body">
            <TextArea id="goal" label="O que você quer resolver?" value={a.objetivo.goal} maxLength={500} onRead={pulse('objetivo')}
              hint="Escreva como você explicaria para um novo diretor."
              placeholder="Ex.: Reposicionar a marca, entender nossos diferenciais frente à concorrência e estruturar um planejamento estratégico para os próximos três anos."
              onChange={v => set('objetivo', { goal: v })} onBlur={reading.now} />
            <ObjectivePanel reading={reading} objetivo={a.objetivo} onField={setObjetivo} />
          </div>
        </details>

        <Materiais details={details('materiais', 4)} />

        <BoardBlock details={details('board', 5)} board={a.board} onChange={b => set('board', b)} onRead={pulse('board')} />
      </div>

      <Actions>
        <button className="btn-ghost" type="button" onClick={async () => { await flush(); setSavedFlash(true); setTimeout(() => setSavedFlash(false), 1800); }}>
          {savedFlash ? 'Rascunho salvo' : 'Salvar rascunho'}
        </button>
        <button className="btn-primary" type="button" onClick={async () => { await flush(); nav('/strategy'); }}>
          Analisar minha organização<Ic.ArrowRight />
        </button>
      </Actions>
    </section>
  );
}
